/**
 * BusinessPortalGuard
 *
 * Wraps protected business-web routes after `ProtectedRoute` has already
 * confirmed the user is signed in. This extra layer verifies that the
 * authenticated identity has MEMBERSHIP in at least one business tenant
 * (role `business_admin` / `business_user` / `viewer` in a b2b / b2b2c /
 * b2g tenant). If not, the user is redirected to `/no-access`.
 *
 * This is the right place to catch the cross-portal case where a citizen
 * identity signed in on journey-web lands on business-web and has no
 * business entitlement yet.
 *
 * We intentionally treat an errored / loading portals query as "allow" so
 * the user is never trapped in a spinner if `/auth/me/portals` hiccups —
 * the server-side tenant guard remains the source of truth for data
 * access. This guard is a UX affordance, not a security boundary.
 */

import { useMyPortals } from '@tonnie/sdk';
import { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';

import { LoadingSpinner } from '@/components/shell/LoadingSpinner';
import { useAuth } from '@/core/contexts/AuthContext';

export function BusinessPortalGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { data, isLoading, error } = useMyPortals();
  const hasRedirected = useRef(false);

  // Derive access once the query settles.
  // - Undefined data + no error => still loading
  // - Error => allow (don't block on UX guard; backend guards data)
  // - No `business` portal with `hasAccess: true` => redirect to /no-access
  const businessAccess = data?.portals.some((p) => p.portal === 'business' && p.hasAccess);
  const shouldRedirect =
    !isAuthLoading &&
    isAuthenticated &&
    !isLoading &&
    !error &&
    data !== undefined &&
    businessAccess === false;

  useEffect(() => {
    if (shouldRedirect && !hasRedirected.current) {
      hasRedirected.current = true;
      setLocation('/no-access');
    }
  }, [shouldRedirect, setLocation]);

  // Hold the spinner until portal access is DEFINITIVELY resolved — not just
  // while `isLoading` is true. `useMyPortals` is token-gated (enabled:
  // isSignedIn && !!token), so before the token lands it's DISABLED and reports
  // `isLoading: false` with `data: undefined`. If we rendered `children` in that
  // window, CarePage (and every business page) would mount early; then the token
  // arrives, the query enables, `isLoading` flips false→true→false, and this
  // guard swaps children for the spinner and back — REMOUNTING the page and
  // wiping its local UI state (observed as the Care page's active tab resetting
  // from "Talents" mid-task under load; block-15). Treating "authed but not yet
  // resolved" as loading makes the page mount exactly once, after portals
  // resolve. (Matches the intent already documented above: undefined data + no
  // error => still loading.)
  if (isAuthLoading || isLoading || (isAuthenticated && data === undefined && !error)) {
    return <LoadingSpinner />;
  }

  if (shouldRedirect) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
}
